import React, { useState, useEffect, useCallback } from "react";
import { X, Clock } from "lucide-react";
import OvertimeRequestsList from "./OvertimeRequestsList";
import OvertimeRequestDetail from "./OvertimeRequestDetail";

/**
 * OvertimeDrawer
 * Full slide-over panel from the right (or left in RTL).
 * Manages list view ↔ detail view navigation internally.
 */
const OvertimeDrawer = ({
  isOpen,
  onClose,
  requests,
  theme,
  isRTL,
  t,
  language,
  onApprove,
  onReject,
  canApprove = true,
  getConfigForEmployee,
  // Date filter props
  dateRange,
  onDateRangeChange,
  overtimeLoading,
}) => {
  const isDark = theme === "dark";
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // ── Persistent filter state ───────────────────────────────────────────
  const now = new Date();
  const [filterMode, setFilterMode] = useState("day");
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedDay, setSelectedDay] = useState(now.toISOString().split("T")[0]);
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [employeeProfilePics] = useState({});

  // ── Reset filter to "day" (today) every time the drawer opens ─────────
  // filterMode/selectedDay otherwise persist across opens because the
  // component never unmounts — isVisible just toggles what's rendered,
  // it doesn't reset state. Without this, reopening the drawer would keep
  // whatever filter (e.g. "month") was left over from the previous visit.
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      setFilterMode("day");
      setSelectedDay(today.toISOString().split("T")[0]);
      setSelectedMonth(today.getMonth());
      setSelectedYear(today.getFullYear());
    }
  }, [isOpen]);

  // ── Reset filter to "day" (today) every time the drawer opens ─────────
  // filterMode/selectedDay otherwise persist across opens because the
  // component never unmounts — isVisible just toggles what's rendered,
  // it doesn't reset state. Without this, reopening the drawer would keep
  // whatever filter (e.g. "month") was left over from the previous visit.
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      setFilterMode("day");
      setSelectedDay(today.toISOString().split("T")[0]);
      setSelectedMonth(today.getMonth());
      setSelectedYear(today.getFullYear());
    }
  }, [isOpen]);

  // ── Animate open/close ─────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimating(true));
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setSelectedRecord(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ── ESC to close ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        if (selectedRecord) {
          setSelectedRecord(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, selectedRecord, onClose]);

  // ── Lock body scroll ───────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleActionComplete = useCallback(() => {
    setSelectedRecord(null);
  }, []);

  const pendingCount = requests.filter((r) => {
    const overallStatus = r.overallStatus || "pending";
    return overallStatus !== "approved" && overallStatus !== "rejected";
  }).length;

  if (!isVisible) return null;

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────── */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          opacity: isAnimating ? 1 : 0,
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />

      {/* ── Drawer Panel ──────────────────────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          top: 0,
          bottom: 0,
          [isRTL ? "left" : "right"]: 0,
          width: "min(520px, 100vw)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          background: isDark
            ? "linear-gradient(180deg, #0f172a, #1e293b)"
            : "linear-gradient(180deg, #ffffff, #f8fafc)",
          boxShadow: isDark
            ? "-8px 0 40px rgba(0,0,0,0.5)"
            : "-8px 0 40px rgba(0,0,0,0.1)",
          transform: isAnimating
            ? "translateX(0)"
            : isRTL
              ? "translateX(-100%)"
              : "translateX(100%)",
          transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: isRTL ? "row-reverse" : "row",
            flexShrink: 0,
          }}
        >
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "4px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "9px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.1))"
                    : "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.06))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock size={16} color="#f59e0b" strokeWidth={2.5} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                {t.overtimeRequests}
              </h2>
              {pendingCount > 0 && (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: "8px",
                    background: "rgba(245,158,11,0.15)",
                    color: "#f59e0b",
                    fontSize: "12px",
                    fontWeight: 700,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {pendingCount}
                </span>
              )}
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.overtimeRequestsSubtitle}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
              background: "transparent",
              color: isDark ? "#94a3b8" : "#64748b",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)";
              e.currentTarget.style.color = isDark ? "#f1f5f9" : "#1e293b";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = isDark ? "#94a3b8" : "#64748b";
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            padding: "20px 24px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {selectedRecord ? (
            <OvertimeRequestDetail
              record={selectedRecord}
              theme={theme}
              isRTL={isRTL}
              t={t}
              language={language}
              onBack={() => setSelectedRecord(null)}
              onApprove={onApprove}
              onReject={onReject}
              canApprove={canApprove}
              onActionComplete={handleActionComplete}
              getConfigForEmployee={getConfigForEmployee}
            />
          ) : (
            <OvertimeRequestsList
              requests={requests}
              theme={theme}
              isRTL={isRTL}
              t={t}
              language={language}
              onSelectRequest={setSelectedRecord}
              dateRange={dateRange}
              onDateRangeChange={onDateRangeChange}
              overtimeLoading={overtimeLoading}
              filterMode={filterMode}
              setFilterMode={setFilterMode}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              activeStatusTab={activeStatusTab}
              setActiveStatusTab={setActiveStatusTab}
              employeeProfilePics={employeeProfilePics}
            />
          )}
        </div>
      </div>

      {/* ── Custom scrollbar for drawer ────────────────────────────────── */}
      <style>{`
        div[style*="overflowY: auto"]::-webkit-scrollbar {
          width: 5px;
        }
        div[style*="overflowY: auto"]::-webkit-scrollbar-track {
          background: transparent;
        }
        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
          background: ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"};
          border-radius: 3px;
        }
        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)"};
        }
      `}</style>
    </>
  );
};

export default OvertimeDrawer;
